import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AccountsEditorPanel,
  BankReconcilePanel,
  EventSettlementPanel,
  InvoicePanel,
  LedgerPanel,
  ReportsPanel,
  ShopifyPanel,
  VenmoPanel,
} from "@/components/accounting";
import { MappingHealthBanner } from "@/components/accounting/MappingHealthBanner";
import { AccountingSetupWizard } from "@/components/accounting/AccountingSetupWizard";
import { useAccountingHealth } from "@/hooks/useAccountingHealth";
import { Button } from "@/components/ui/button";
import { useSessionUser } from "@/hooks/useSessionUser";
import CapabilityGate from "@/components/CapabilityGate";

type AccountingTab = {
  id: string;
  label: string;
  render: () => JSX.Element;
};

export default function AccountingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") ?? "ledger";
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const { hasPermission, user } = useSessionUser();
  // The bookkeeping tools (ledger, bank, invoices, reports) use one shared set of
  // books and are limited to the platform owner until they are per-customer.
  const isOwner = Boolean(user?.is_owner);
  const canViewFinancials = hasPermission("view_financials") && isOwner;
  const canManageFinancials = hasPermission("manage_financials");
  const { health, loading: healthLoading, error: healthError, refresh } = useAccountingHealth(canViewFinancials);
  const [showWizard, setShowWizard] = useState(false);
  const [autoPrompted, setAutoPrompted] = useState(false);
  const [wizardSource, setWizardSource] = useState<"auto" | "manual" | null>(null);

  useEffect(() => {
    if (health && !health.ok && !autoPrompted) {
      setShowWizard(true);
      setWizardSource("auto");
      setAutoPrompted(true);
    }
    if (health?.ok && showWizard && wizardSource === "auto") {
      setShowWizard(false);
      setWizardSource(null);
    }
  }, [health, autoPrompted, showWizard, wizardSource]);

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [searchParams, activeTab]);

  const tabs = useMemo<AccountingTab[]>(
    () => [
      { id: "ledger", label: "Ledger", render: () => <LedgerPanel /> },
      { id: "reports", label: "Reports", render: () => <ReportsPanel /> },
      { id: "accounts", label: "Accounts", render: () => <AccountsEditorPanel /> },
      {
        id: "bank",
        label: "Bank",
        render: () => (
          <BankReconcilePanel
            health={health}
            healthLoading={healthLoading}
            healthError={healthError}
            showHealthBanner={false}
          />
        ),
      },
      {
        id: "shopify",
        label: "Shopify",
        render: () => (
          <ShopifyPanel
            health={health}
            healthLoading={healthLoading}
            healthError={healthError}
            showHealthBanner={false}
          />
        ),
      },
      {
        id: "venmo",
        label: "Venmo",
        render: () => (
          <VenmoPanel
            health={health}
            healthLoading={healthLoading}
            healthError={healthError}
            showHealthBanner={false}
          />
        ),
      },
      {
        id: "events",
        label: "Settlements",
        render: () => (
          <CapabilityGate flag="canUseSettlementPreview">
            <EventSettlementPanel standalone={false} />
          </CapabilityGate>
        ),
      },
      { id: "invoices", label: "Invoices", render: () => <InvoicePanel /> },
    ],
    [health, healthError, healthLoading],
  );

  if (!isOwner) {
    return (
      <div className="aa-page-shell space-y-4">
        <h1 className="aa-page-title">Business</h1>
        <div className="aa-glass-card space-y-3 rounded-2xl p-6" role="status" aria-label="Business overview">
          <p className="font-medium text-foreground">Track the money for each show in Shows.</p>
          <p className="text-sm text-muted-foreground">
            Open a show to record the guarantee, ticket sales, merch and expenses. Full bookkeeping — invoices,
            reports and bank reconciliation — is coming to Business soon.
          </p>
          <Link
            to="/shows"
            className="inline-flex rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            Go to Shows
          </Link>
        </div>
      </div>
    );
  }

  if (!canViewFinancials) {
    return (
      <div className="aa-page-shell flex flex-col items-center justify-center gap-4 py-12 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full border border-border bg-muted">
          <svg className="h-6 w-6 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold">Business access required</h2>
        <p className="max-w-sm text-sm text-muted-foreground">
          You don't have permission to view financial data. Contact your organization owner to request access.
        </p>
      </div>
    );
  }

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    const next = new URLSearchParams(searchParams);
    next.set("tab", tabId);
    setSearchParams(next, { replace: true });
  };

  const openWizard = (source: "auto" | "manual") => {
    if (!canManageFinancials) return;
    setWizardSource(source);
    setShowWizard(true);
  };

  return (
    <div className="aa-page-shell space-y-4">
      <MappingHealthBanner
        health={health}
        loading={healthLoading}
        error={healthError}
        onViewRequirements={() => {
          openWizard("manual");
        }}
      />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="aa-page-title">Business</h1>
          <p className="text-sm text-muted-foreground">
            What's happening financially around your live career: settlements, invoices, payouts and reports.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!canManageFinancials && (
            <p className="text-xs text-primary">Read-only access.</p>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => openWizard("manual")}
            disabled={!canManageFinancials}
            title={canManageFinancials ? undefined : "Requires manage_financials permission"}
          >
            Setup wizard
          </Button>
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 text-sm sm:flex-wrap sm:overflow-visible sm:pb-0">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleTabChange(tab.id)}
            className={`rounded-full px-3 py-1 transition ${
              tab.id === activeTab
                ? "border border-primary/60 bg-primary/15 text-foreground"
                : "border border-border/50 bg-background/30 text-foreground/70 hover:border-primary/40 hover:bg-background/40"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div>{tabs.map((tab) => (tab.id === activeTab ? <div key={tab.id}>{tab.render()}</div> : null))}</div>

      <AccountingSetupWizard
        open={showWizard && canManageFinancials}
        health={health}
        loading={healthLoading}
        onClose={() => {
          setShowWizard(false);
          setWizardSource(null);
        }}
        onRefresh={refresh}
      />
    </div>
  );
}
